import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Upload, X, Loader2, MapPin, Calendar, Pound } from 'lucide-react';
import { serviceMarketplaceAPI, serviceRequestAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthToken } = useAuth();

  const preselectedCategory = searchParams.get('category') || '';
  const preselectedProvider = searchParams.get('provider') || '';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    category: preselectedCategory,
    subcategory: '',
    title: '',
    description: '',
    postcode: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    budgetMin: '',
    budgetMax: '',
  });

  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    serviceMarketplaceAPI.getCategories().then(setCategories).catch(console.error);
  }, []);

  const selectedCategoryData = categories.find(c => c.id === form.category);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    if (!form.category || !form.description || !form.postcode) {
      alert('Please fill in category, description, and postcode.');
      return;
    }

    setLoading(true);
    try {
      const token = getAuthToken();
      const payload = {
        category: form.category,
        subcategory: form.subcategory,
        title: form.title,
        description: form.description,
        location: {
          postcode: form.postcode,
          address: form.address,
        },
        preferredDate: form.preferredDate || null,
        preferredTime: form.preferredTime || null,
        budget: {
          min: form.budgetMin ? parseFloat(form.budgetMin) : null,
          max: form.budgetMax ? parseFloat(form.budgetMax) : null,
        },
        photos: photos.map(p => p.preview),
      };

      await serviceRequestAPI.create(payload, token);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit request:', err);
      alert(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
          <p className="text-gray-500 mb-6">
            Your service request has been posted. Matching providers in your area will be able to send you quotes.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/services/my-requests')}
              className="px-6 py-3 bg-[#1F3A68] text-white font-semibold rounded-lg hover:bg-[#162d52] transition-colors"
            >
              View My Requests
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-6 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <ChevronLeft size={18} /> Back
          </button>
          <h1 className="font-bold text-gray-900">Post a Service Request</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Category */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold text-gray-900 mb-4">What do you need?</h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Category *</label>
              <select
                value={form.category}
                onChange={e => { updateForm('category', e.target.value); updateForm('subcategory', ''); }}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
                required
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            {selectedCategoryData?.subcategories?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Specialization</label>
                <select
                  value={form.subcategory}
                  onChange={e => updateForm('subcategory', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
                >
                  <option value="">Any</option>
                  {selectedCategoryData.subcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Title (optional)</label>
              <input
                type="text"
                value={form.title}
                onChange={e => updateForm('title', e.target.value)}
                placeholder="e.g. End of Tenancy Deep Clean"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Description *</label>
              <textarea
                value={form.description}
                onChange={e => updateForm('description', e.target.value)}
                placeholder="Describe what you need done, any special requirements, size of area, etc."
                rows={4}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68] resize-none"
                required
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin size={18} className="text-[#1F3A68]" /> Location
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Postcode *</label>
              <input
                type="text"
                value={form.postcode}
                onChange={e => updateForm('postcode', e.target.value)}
                placeholder="e.g. SW1A 1AA"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Address (optional)</label>
              <input
                type="text"
                value={form.address}
                onChange={e => updateForm('address', e.target.value)}
                placeholder="Full address or area name"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              />
            </div>
          </div>
        </div>

        {/* Date & Budget */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold text-gray-900 mb-4">Schedule & Budget</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Preferred Date</label>
              <input
                type="date"
                value={form.preferredDate}
                onChange={e => updateForm('preferredDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Preferred Time</label>
              <select
                value={form.preferredTime}
                onChange={e => updateForm('preferredTime', e.target.value)}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              >
                <option value="">Flexible</option>
                <option value="morning">Morning (8am-12pm)</option>
                <option value="afternoon">Afternoon (12pm-5pm)</option>
                <option value="evening">Evening (5pm-9pm)</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Min Budget (£)</label>
              <input
                type="number"
                value={form.budgetMin}
                onChange={e => updateForm('budgetMin', e.target.value)}
                placeholder="Min"
                min="0"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Max Budget (£)</label>
              <input
                type="number"
                value={form.budgetMax}
                onChange={e => updateForm('budgetMax', e.target.value)}
                placeholder="Max"
                min="0"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1F3A68]/20 focus:border-[#1F3A68]"
              />
            </div>
          </div>
        </div>

        {/* Photos */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold text-gray-900 mb-4">Photos (optional)</h2>
          <p className="text-sm text-gray-400 mb-3">Add photos to help providers understand the job</p>

          <div className="flex flex-wrap gap-3">
            {photos.map((photo, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border">
                <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1F3A68] hover:bg-gray-50 transition-colors">
                <Upload size={20} className="text-gray-400 mb-1" />
                <span className="text-[10px] text-gray-400">Upload</span>
                <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
              </label>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-[#1F3A68] text-white font-semibold rounded-lg hover:bg-[#162d52] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : 'Post Service Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceRequestPage;
